<template>
  <div class="emission-success-container">
    <div class="success-content">
      <!-- Icono de éxito animado -->
      <div class="success-icon-wrapper">
        <div class="success-circle">
          <el-icon class="success-check-icon">
            <Check />
          </el-icon>
        </div>
      </div>

      <!-- Título y mensaje -->
      <h1 class="success-title">{{ successTitle }}</h1>
      <p class="success-subtitle">
        {{ successSubtitle }}
      </p>

      <!-- Información de la póliza/cotización -->
      <div v-if="policyData" class="policy-info-card">
        <div class="policy-info-header">
          <h2 class="policy-info-title">{{ infoSectionTitle }}</h2>
        </div>
        <div class="policy-info-body">
          <div class="info-row" v-if="showPolicyNumber">
            <span class="info-label">Número de Póliza:</span>
            <span class="info-value policy-number">{{ policyData.policyNumber }}</span>
          </div>
          <div class="info-row" v-if="vehicleInfo">
            <span class="info-label">Vehículo:</span>
            <span class="info-value">{{ vehicleInfo }}</span>
          </div>
          <div class="info-row" v-if="policyData.planType">
            <span class="info-label">Tipo de Plan:</span>
            <span class="info-value">{{ policyData.planType }}</span>
          </div>
          <div class="info-row" v-if="policyData.primaTotal">
            <span class="info-label">Prima Total:</span>
            <span class="info-value price">{{ formatPrice(policyData.primaTotal) }}</span>
          </div>
        </div>
      </div>

      <!-- Documentos disponibles -->
      <div v-if="hasDocuments" class="documents-section">
        <h3 class="documents-title">
          <el-icon class="documents-icon">
            <Download />
          </el-icon>
          Documentos Disponibles
        </h3>
        <p class="documents-subtitle">
          {{ documentsSubtitle }}
        </p>
        <div class="documents-grid">
          <div v-for="(documento, index) in documentos" :key="index" class="document-card">
            <div class="document-icon">
              <el-icon>
                <Document />
              </el-icon>
            </div>
            <div class="document-info">
              <span class="document-name">{{ documento.Nombre || documento.Tipo || 'Documento' }}</span>
              <span class="document-type" v-if="documento.Tipo">{{ documento.Tipo }}</span>
            </div>
            <el-button type="primary" size="default"
              @click="handleDownloadDocument(documento.Guid_Documento, documento.Nombre)" class="download-doc-button">
              <el-icon>
                <Download />
              </el-icon>
              Descargar
            </el-button>
          </div>
        </div>
      </div>

      <!-- Acciones -->
      <div class="actions-section">
        <el-button
          v-if="SHOW_EMAIL_SHARE"
          type="primary"
          size="large"
          @click="handleSendEmail"
          :loading="isSendingEmail"
          class="action-button primary-action"
        >
          <el-icon class="button-icon">
            <Message />
          </el-icon>
          <span class="button-text">{{ emailButtonText }}</span>
        </el-button>
        <el-button type="success" size="large" @click="handleDownloadAll" :loading="isDownloadingAll"
          class="action-button success-action">
          <el-icon class="button-icon">
            <Download />
          </el-icon>
          <span class="button-text">{{ downloadButtonText }}</span>
        </el-button>
        <el-button type="default" size="large" @click="handleBackToComparison" class="action-button">
          <span class="button-text">Volver a Comparación</span>
        </el-button>
      </div>

      <!-- Mensaje informativo -->
      <div class="info-message">
        <el-icon class="info-icon">
          <InfoFilled />
        </el-icon>
        <p v-html="infoMessageText.replace(/\n/g, '<br>')"></p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Check, Download, Message, Document, InfoFilled } from '@element-plus/icons-vue';
import { useQuoteStore } from '@/stores/quoteStore';
import { SHOW_EMAIL_SHARE } from '@/config/features';
import type { EmissionSuccessProps } from './emissionSuccess/types';
import { describeVehicle, formatPrice } from './emissionSuccess/display';
import { maintainExpandMode } from './emissionSuccess/expandMode';
import { useEmissionTexts } from './emissionSuccess/useEmissionTexts';
import { useEmissionDownloads } from './emissionSuccess/useEmissionDownloads';
import { useEmissionEmail } from './emissionSuccess/useEmissionEmail';

const props = withDefaults(defineProps<EmissionSuccessProps>(), {
  quotesResponse: null,
  policyData: undefined,
  documentos: () => [],
  selectedPlanType: 'Amplia',
  selectedPaymentMethod: 'Contado',
  selectedCompany: undefined,
  allowEmission: true,
});

const emit = defineEmits<{
  back: [];
  close: [];
  editDriver: [];
}>();

const quoteStore = useQuoteStore();
const vehicleInfo = computed(() => describeVehicle(quoteStore.quoteData));

const {
  successTitle,
  successSubtitle,
  infoSectionTitle,
  emailButtonText,
  documentsSubtitle,
  downloadButtonText,
  infoMessageText,
  hasDocuments,
  showPolicyNumber,
} = useEmissionTexts(props);
const { isDownloadingAll, handleDownloadDocument, handleDownloadAll } = useEmissionDownloads(props);
const { isSendingEmail, handleSendEmail } = useEmissionEmail(props, () => emit('editDriver'));

const handleBackToComparison = (): void => {
  maintainExpandMode(); // Mantener modo expandido
  emit('back');
};
</script>

<style scoped src="./emissionSuccess/EmissionSuccess.css"></style>
